'use client';

import { useToast } from '@/components/ui/use-toast';
import { setBasket } from '@/lib/BasketStorage';
import { getOsType } from '@/lib/CookieUtils';
import { hashSHA256 } from '@/lib/crypto/SHA256';
import { Endpoints } from '@/lib/Endpoints';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { GTMEventType } from '@/lib/GTMEventType';
import { isAppClient, setIsAnonymous, setLocalToken, setLocalUser } from '@/lib/LocalStorage';
import { getTokenForApp } from '@/lib/CookieUtils';
import { checkRPC } from '@/lib/utils';
import { applyNativeAttributionPromoCode } from '@/lib/nativeAttributionPromoCode';
import { emailRegex } from '@/lib/PhoneUtils';
import { Requester } from '@/lib/Requester';
import { BasketResponseModel } from '@/models/Basket';
import { GenericResponse } from '@/models/GenericResponse';
import { LoginResponse } from '@/models/User';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ErrorHandler } from '../lib/ErrorHandler';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from './ui/form';
import { Input } from './ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Spinner } from './ui/spinner';

const DefaultLoginState = {
  loginLoading: false,
  otpRequestLoading: false,
};

export default function LoginForm({
  onSignup,
}: {
  onSignup?: (prefill?: { type: 'email' | 'phone'; value: string }) => void;
}) {
  const { toast } = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [loginState, setLoginState] = useState(DefaultLoginState);
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState(false);
  const [pageOsType, setPageOsType] = useState<string | undefined>(undefined);
  const [loginStep, setLoginStep] = useState<'identifier' | 'credential' | 'setPassword'>('identifier');
  const [credentialType, setCredentialType] = useState<'email' | 'phone' | null>(null);
  const [otpCooldownLeft, setOtpCooldownLeft] = useState(0);
  const [otpChannelSwitchCooldownLeft, setOtpChannelSwitchCooldownLeft] = useState(0);
  const [userNotFound, setUserNotFound] = useState<{
    type: 'email' | 'phone';
    value: string;
  } | null>(null);
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);

  useEffect(() => {
    setPageOsType(getOsType());
  }, []);

  useEffect(() => {
    if (otpCooldownLeft <= 0) return;
    const timer = setInterval(() => {
      setOtpCooldownLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldownLeft]);

  useEffect(() => {
    if (otpChannelSwitchCooldownLeft <= 0) return;
    const timer = setInterval(() => {
      setOtpChannelSwitchCooldownLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpChannelSwitchCooldownLeft]);

  const FormSchema = z.object({
    identifier: z.string().min(1, {
      message: 'Cep telefonu ya da e-posta giriniz',
    }),
    password: z.string().optional(),
    otp: z.string().optional(),
    rememberMe: z.boolean(),
    newPassword: z.string().optional(),
    confirmPassword: z.string().optional(),
    setupEmail: z.string().optional(),
    setupTitle: z.string().optional(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      identifier: '',
      password: '',
      otp: '',
      rememberMe: false,
      newPassword: '',
      confirmPassword: '',
      setupEmail: '',
      setupTitle: '',
    },
  });

  const normalizePhoneIdentifier = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const normalized = digits.replace(/^(?:0090|90|0)/, '');
    if (normalized.length !== 10 || !normalized.startsWith('5')) {
      return null;
    }
    return normalized;
  };

  const parseIdentifier = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (trimmed.includes('@')) {
      if (!emailRegex.test(trimmed)) return null;
      return {
        type: 'email' as const,
        normalized: trimmed.toLowerCase(),
      };
    }

    const normalizedPhone = normalizePhoneIdentifier(trimmed);
    if (!normalizedPhone) return null;
    return {
      type: 'phone' as const,
      normalized: normalizedPhone,
    };
  };

  async function loadBasketFromBackend() {
    try {
      const { data: result } = await Requester.get<GenericResponse<BasketResponseModel>>(
        Endpoints.GET_BASKET,
      );

      setBasket(result.data.basket, 'LoginForm - loadBasketFromBackend');
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    }
  }

  const formatCooldown = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const getOtpRequestErrorMessage = (statusCode: number, fallback: string) => {
    if (statusCode === 103) return 'Kullanıcı bulunamadı.';
    if (statusCode === 6015) return 'Çok sık kod istediniz. Lütfen 2 dakika sonra tekrar deneyin.';
    return fallback || 'Bir hata oluştu';
  };

  const getLoginErrorMessage = (statusCode: number, fallback: string) => {
    if (statusCode === 103) return 'Kullanıcı bulunamadı.';
    if (statusCode === 6009) return 'Giriş bilgileri hatalı.';
    if (statusCode === 6016) return 'OTP süresi doldu. Lütfen yeni kod isteyin.';
    if (statusCode === 6018) {
      return 'Telefon numaranızla OTP ile giriş yapıp şifre belirleyin.';
    }
    return fallback || 'Bir hata oluştu';
  };

  const normalizeStatusCode = (value: unknown): number => {
    const code = Number(value);
    return Number.isFinite(code) ? code : -1;
  };

  const getErrorStatusCode = (error: any): number => {
    return normalizeStatusCode(
      error?.response?.data?.meta?.statusCode ??
        error?.response?.data?.statusCode ??
        error?.statusCode ??
        error?.code,
    );
  };

  const requestOtpCode = async (msisdn: string, channel: 'sms' | 'whatsapp' = 'sms') => {
    const isWhatsapp = channel === 'whatsapp';
    if (!isWhatsapp && otpCooldownLeft > 0) {
      toast({
        variant: 'destructive',
        description: `Yeni kod için ${formatCooldown(otpCooldownLeft)} bekleyin.`,
      });
      return false;
    }
    if (isWhatsapp && otpChannelSwitchCooldownLeft > 0) {
      toast({
        variant: 'destructive',
        description: `WhatsApp için ${otpChannelSwitchCooldownLeft} sn bekleyin.`,
      });
      return false;
    }

    try {
      setLoginState((prev) => ({ ...prev, otpRequestLoading: true }));
      const { data: result } = await Requester.post<GenericResponse<any>>(
        Endpoints.LOGIN_OTP_REQUEST,
        {
          msisdn,
          channel,
        },
      );

      const responseStatusCode = normalizeStatusCode(result?.meta?.statusCode);

      if (responseStatusCode === 0) {
        if (isWhatsapp) {
          setOtpChannelSwitchCooldownLeft(30);
        } else {
          setOtpCooldownLeft(120);
          setOtpChannelSwitchCooldownLeft(30);
        }
        toast({
          variant: 'default',
          description:
            channel === 'whatsapp'
              ? 'OTP kodu WhatsApp üzerinden gönderildi.'
              : 'OTP kodu telefonunuza gönderildi.',
        });
        return true;
      }

      if (responseStatusCode === 103) {
        setUserNotFound({ type: 'phone', value: msisdn });
        return false;
      }

      if (responseStatusCode === 6015) {
        if (isWhatsapp) {
          setOtpChannelSwitchCooldownLeft(30);
        } else {
          setOtpCooldownLeft(120);
          setOtpChannelSwitchCooldownLeft(30);
        }
      }

      toast({
        variant: 'destructive',
        description: getOtpRequestErrorMessage(responseStatusCode, result?.meta?.message),
      });
      return false;
    } catch (error) {
      const rawStatusCode = getErrorStatusCode(error);
      const { statusCode, message } = ErrorHandler.getMessage(error);
      const effectiveStatusCode =
        normalizeStatusCode(statusCode) !== -1 ? statusCode : rawStatusCode;

      if (effectiveStatusCode === 103) {
        setUserNotFound({ type: 'phone', value: msisdn });
        return false;
      }
      if (effectiveStatusCode === 6015) {
        if (isWhatsapp) {
          setOtpChannelSwitchCooldownLeft(30);
        } else {
          setOtpCooldownLeft(120);
          setOtpChannelSwitchCooldownLeft(30);
        }
      }
      toast({
        variant: 'destructive',
        description: getOtpRequestErrorMessage(effectiveStatusCode, message),
      });
      return false;
    } finally {
      setLoginState((prev) => ({ ...prev, otpRequestLoading: false }));
    }
  };

  const navigateAfterLogin = () => {
    const loginDest = (() => {
      try {
        const stored = sessionStorage.getItem('hzc_login_dest') || '';
        if (stored.startsWith('/') && !stored.startsWith('//')) {
          sessionStorage.removeItem('hzc_login_dest');
          return stored;
        }
      } catch {}
      const raw = searchParams?.get('returnTo') || '';
      if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
      return '';
    })();
    const isApp = isAppClient() || checkRPC('bridge') || Boolean(loginDest);
    router.replace(loginDest || (isApp ? '/profilim' : '/'));
  };

  async function submitSetPassword() {
    if (setPasswordLoading) return;

    const newPassword = (form.getValues('newPassword') || '').trim();
    const confirmPassword = (form.getValues('confirmPassword') || '').trim();
    const setupEmail = (form.getValues('setupEmail') || '').trim();
    const setupTitle = (form.getValues('setupTitle') || '').trim();

    form.clearErrors(['newPassword', 'confirmPassword', 'setupEmail']);

    if (newPassword.length < 6) {
      form.setError('newPassword', {
        type: 'manual',
        message: 'En az 6 haneli bir şifre giriniz',
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      form.setError('confirmPassword', {
        type: 'manual',
        message: 'Şifreler eşleşmiyor',
      });
      return;
    }
    if (setupEmail && !emailRegex.test(setupEmail)) {
      form.setError('setupEmail', {
        type: 'manual',
        message: 'Geçerli bir e-posta giriniz',
      });
      return;
    }

    try {
      setSetPasswordLoading(true);
      const { data: result } = await Requester.post<
        GenericResponse<{ status: boolean; needsPasswordSetup: boolean; user: LoginResponse['user'] }>
      >(Endpoints.SET_PASSWORD_AFTER_PANEL_CLAIM, {
        password: newPassword,
        email: setupEmail || undefined,
        title: setupTitle || undefined,
      });

      const responseStatusCode = normalizeStatusCode(result?.meta?.statusCode);
      if (responseStatusCode !== 0) {
        toast({
          variant: 'destructive',
          description: result?.meta?.message || 'Şifre kaydedilemedi',
        });
        return;
      }

      if (result?.data?.user) {
        setLocalUser(result.data.user);
      }
      toast({
        description: 'Şifreniz kaydedildi.',
      });
      navigateAfterLogin();
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message || 'Şifre kaydedilemedi',
      });
    } finally {
      setSetPasswordLoading(false);
    }
  }

  async function onSubmit(values: z.infer<typeof FormSchema>) {
    if (loginState.loginLoading) {
      return;
    }

    const parsed = parseIdentifier(values.identifier);
    if (!parsed) {
      form.setError('identifier', {
        type: 'manual',
        message: 'Geçerli bir cep telefonu ya da e-posta giriniz',
      });
      return;
    }

    const credential = parsed.type === 'phone' ? (values.otp || '').trim() : values.password || '';

    if (parsed.type === 'email' && credential.length < 6) {
      form.setError('password', {
        type: 'manual',
        message: 'En az 6 haneli bir şifre giriniz',
      });
      return;
    }

    if (parsed.type === 'phone' && !/^\d{4,6}$/.test(credential)) {
      form.setError('otp', {
        type: 'manual',
        message: 'Geçerli bir OTP kodu giriniz',
      });
      return;
    }

    try {
      setLoginState((prev) => ({
        ...prev,
        loginLoading: true,
      }));

      const { data: result } = await Requester.post<GenericResponse<LoginResponse>>(
        Endpoints.LOGIN,
        {
          email: parsed.normalized,
          password: credential,
          device: {
            osType: pageOsType || 'web',
          },
        },
      );

      const responseStatusCode = normalizeStatusCode(result?.meta?.statusCode);

      sendGTMEvent({
        event: GTMEventType.LOGIN_SUCCESS,
        ecommerce: {
          user_id: result.data.user.id,
          user_mail: parsed.type === 'email' ? hashSHA256(parsed.normalized) : '',
          user_phone: result.data.user.msisdn
            ? hashSHA256(`+90${result.data.user.msisdn.slice(-10)}`)
            : '',
        },
      });

      if (responseStatusCode === 0) {
        setLocalUser(result.data.user);
        setIsAnonymous(false);
        const cookieToken = getTokenForApp();
        if (cookieToken) {
          setLocalToken(cookieToken);
        }
        applyNativeAttributionPromoCode(result.data.nativeAttributionPromoCode);
        try {
          const text = String((result.data as any).nativeAttributionInfoPopup || '').trim();
          if (text && typeof window !== 'undefined') {
            (window as any).__hzcSetNativeAttributionInfoPopup?.(text);
          }
        } catch {}
        await loadBasketFromBackend();

        if (result.data.needsPasswordSetup) {
          form.setValue('setupTitle', result.data.user?.title || '');
          form.setValue('setupEmail', result.data.user?.email || '');
          setLoginStep('setPassword');
          toast({
            description: 'Devam etmek için şifre belirleyin.',
          });
          return;
        }

        navigateAfterLogin();
      } else {
        if (responseStatusCode === 103) {
          setUserNotFound({
            type: parsed.type,
            value: parsed.normalized,
          });
          return;
        }
        toast({
          variant: 'destructive',
          description: getLoginErrorMessage(responseStatusCode, result?.meta?.message),
        });
      }
    } catch (error) {
      const rawStatusCode = getErrorStatusCode(error);
      const { statusCode, message } = ErrorHandler.getMessage(error);
      const effectiveStatusCode =
        normalizeStatusCode(statusCode) !== -1 ? statusCode : rawStatusCode;

      if (effectiveStatusCode === 103) {
        setUserNotFound({
          type: parsed.type,
          value: parsed.normalized,
        });
        return;
      }
      toast({
        variant: 'destructive',
        description: getLoginErrorMessage(effectiveStatusCode, message),
      });
    } finally {
      setLoginState((prev) => ({
        ...prev,
        loginLoading: false,
      }));
    }
  }

  const onPrimaryAction = async () => {
    if (loginState.loginLoading || loginState.otpRequestLoading || setPasswordLoading) return;

    if (loginStep === 'setPassword') {
      await submitSetPassword();
      return;
    }

    const identifier = form.getValues('identifier');
    const parsed = parseIdentifier(identifier);

    if (!parsed) {
      form.setError('identifier', {
        type: 'manual',
        message: 'Geçerli bir cep telefonu ya da e-posta giriniz',
      });
      return;
    }

    form.clearErrors('identifier');
    setUserNotFound(null);

    if (loginStep === 'identifier') {
      if (parsed.type === 'phone') {
        const otpRequested = await requestOtpCode(parsed.normalized, 'sms');
        if (otpRequested) {
          setCredentialType('phone');
          setLoginStep('credential');
        }
        return;
      }

      setCredentialType('email');
      setLoginStep('credential');
      return;
    }

    await form.handleSubmit(onSubmit)();
  };

  const changeIdentifier = () => {
    setUserNotFound(null);
    setLoginStep('identifier');
    setCredentialType(null);
    setOtpCooldownLeft(0);
    setOtpChannelSwitchCooldownLeft(0);
    form.setValue('password', '');
    form.setValue('otp', '');
    form.setValue('newPassword', '');
    form.setValue('confirmPassword', '');
    form.setValue('setupEmail', '');
    form.setValue('setupTitle', '');
  };

  const goToSignupWithPrefill = () => {
    if (!userNotFound) {
      onSignup?.();
      return;
    }
    onSignup?.({
      type: userNotFound.type,
      value: userNotFound.value,
    });
  };

  return (
    <div className="flex flex-col">
      <h2 className="md:flex text-4xl sm:text-5xl md:text-[56px] font-semibold text-left text-hzc_purple_500 mb-4 md:mb-12">
        {loginStep === 'setPassword' ? 'Şifre Belirle' : 'Üye Girişi'}
      </h2>
      <Form {...form}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void onPrimaryAction();
          }}
        >
          {!userNotFound && loginStep !== 'setPassword' && (
            <FormField
              control={form.control}
              name="identifier"
              render={({ field }) => (
                <FormItem>
                  <span className="text-sm text-hzc_beige_700 font-medium">
                    Cep telefonu ya da e-posta adresi
                  </span>
                  <FormControl>
                    <Input
                      className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                      {...field}
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      onChange={(e) => {
                        field.onChange(e);
                        form.clearErrors('identifier');
                        setLoginStep('identifier');
                        setCredentialType(null);
                        setOtpCooldownLeft(0);
                        setUserNotFound(null);
                        form.setValue('password', '');
                        form.setValue('otp', '');
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          )}

          {loginStep === 'setPassword' && (
            <p className="text-sm text-hzc_beige_700 mb-2">
              Devam etmek için gerekli bilgileri giriniz.
            </p>
          )}

          {userNotFound && (
            <div className="flex flex-col gap-2 rounded-xl border border-hzc_beige_300 bg-white p-6">
              <div className="text-sm text-hzc_beige_900">
                <span>{userNotFound.value}</span>{' '}
                <button
                  type="button"
                  className="text-green-500 underline"
                  onClick={changeIdentifier}
                >
                  (Değiştir)
                </button>
              </div>
              <button
                type="button"
                className="text-sm text-hzc_purple_500 text-left underline mt-2"
                onClick={goToSignupWithPrefill}
              >
                Bu bilgiyle bir üyelik bulamadık. Yeni hesap oluşturmak için tıklayın.
              </button>
            </div>
          )}

          {loginStep === 'setPassword' && (
            <>
              <FormField
                control={form.control}
                name="setupTitle"
                render={({ field }) => (
                  <FormItem>
                    <span className="text-sm text-hzc_beige_700 font-medium">Ad Soyad</span>
                    <FormControl>
                      <Input
                      required
                        className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                        {...field}
                        autoComplete="name"
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="setupEmail"
                render={({ field }) => (
                  <FormItem>
                    <span className="text-sm text-hzc_beige_700 font-medium">E-posta</span>
                    <FormControl>
                      <Input
                      required
                        type="email"
                        className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                        {...field}
                        autoComplete="email"
                        autoCapitalize="none"
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <span className="text-sm text-hzc_beige_700 font-medium">Yeni Şifre</span>
                    <FormControl>
                      <Input
                        type="password"
                        className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                        {...field}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <span className="text-sm text-hzc_beige_700 font-medium">Şifre Tekrar</span>
                    <FormControl>
                      <Input
                        type="password"
                        className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                        {...field}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </>
          )}

          {!userNotFound && loginStep === 'credential' && credentialType === 'email' && (
            <>
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <span className="text-sm text-hzc_beige_700 font-medium">Şifre</span>
                    <FormControl>
                      <Input
                        type="password"
                        className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <div className="flex justify-start items-center mt-2">
                <FormField
                  control={form.control}
                  name="rememberMe"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="flex items-center gap-4">
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="w-8 h-8 flex-shrink-0 data-[state=checked]:bg-hzc_purple_500 bg-transparent text-bg-hzc_purple_500 border-hzc_beige_900"
                          />
                          <span className="text-sm text-hzc_beige_700">Beni Hatırla</span>
                        </div>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}

          {!userNotFound && loginStep === 'credential' && credentialType === 'phone' && (
            <>
              <FormField
                control={form.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <span className="text-sm text-hzc_beige_700 font-medium">Doğrulama Kodu</span>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        value={field.value || ''}
                        onChange={(value) => {
                          field.onChange(value);
                          form.clearErrors('otp');
                        }}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="one-time-code"
                      >
                        <InputOTPGroup className="mx-auto">
                          <InputOTPSlot
                            index={0}
                            className="h-12 w-12 border border-hzc_beige_300 bg-white text-lg text-hzc_beige_900"
                          />
                          <InputOTPSlot
                            index={1}
                            className="h-12 w-12 border border-hzc_beige_300 bg-white text-lg text-hzc_beige_900"
                          />
                          <InputOTPSlot
                            index={2}
                            className="h-12 w-12 border border-hzc_beige_300 bg-white text-lg text-hzc_beige_900"
                          />
                          <InputOTPSlot
                            index={3}
                            className="h-12 w-12 border border-hzc_beige_300 bg-white text-lg text-hzc_beige_900"
                          />
                          <InputOTPSlot
                            index={4}
                            className="h-12 w-12 border border-hzc_beige_300 bg-white text-lg text-hzc_beige_900"
                          />
                          <InputOTPSlot
                            index={5}
                            className="h-12 w-12 border border-hzc_beige_300 bg-white text-lg text-hzc_beige_900"
                          />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage className="text-xs text-center" />
                  </FormItem>
                )}
              />
              <div className="text-center text-xs text-hzc_beige_700">
                {otpCooldownLeft > 0 ? (
                  <span>Yeni kod için {formatCooldown(otpCooldownLeft)} bekleyin.</span>
                ) : (
                  <button
                    type="button"
                    className="underline text-hzc_purple_500"
                    onClick={() => {
                      const parsed = parseIdentifier(form.getValues('identifier'));
                      if (parsed?.type === 'phone') {
                        void requestOtpCode(parsed.normalized, 'sms');
                      }
                    }}
                    disabled={loginState.otpRequestLoading}
                  >
                    {loginState.otpRequestLoading ? 'Kod gönderiliyor...' : 'Kodu tekrar gönder'}
                  </button>
                )}
              </div>
              <div className="text-center text-xs text-hzc_beige_700 mt-2">
                <button
                  type="button"
                  className="underline text-hzc_purple_500"
                  onClick={() => {
                    const parsed = parseIdentifier(form.getValues('identifier'));
                    if (parsed?.type === 'phone') {
                      void requestOtpCode(parsed.normalized, 'whatsapp');
                    }
                  }}
                  disabled={loginState.otpRequestLoading || otpChannelSwitchCooldownLeft > 0}
                >
                  {otpChannelSwitchCooldownLeft > 0
                    ? `WhatsApp üzerinden al (${otpChannelSwitchCooldownLeft} sn)`
                    : 'WhatsApp üzerinden al'}
                </button>
              </div>
            </>
          )}

          {!userNotFound && (
            <>
              <Button
                type="submit"
                className="hover:scale-105 transition-all duration-300 w-full h-14 px-6 py-4 rounded-full bg-hzc_purple_500 hover:bg-hzc_purple_500"
              >
                <div className="flex gap-2 justify-center items-center">
                  {(loginState.loginLoading ||
                    loginState.otpRequestLoading ||
                    setPasswordLoading) && <Spinner className="size-6 text-white" />}
                  <span className="text-hzc_beige_100">
                    {loginStep === 'identifier'
                      ? 'Devam Et'
                      : loginStep === 'setPassword'
                        ? 'Şifreyi Kaydet'
                        : 'Giriş Yap'}
                  </span>
                </div>
              </Button>

              {loginStep !== 'setPassword' && (
                <div className="flex justify-between items-center mt-8 md:mt-4 px-2">
                  <a
                    className="align-baseline font-medium text-xs text-hzc_purple_500 hover:cursor-pointer hover:underline"
                    onClick={() => setForgotPasswordDialogOpen(true)}
                  >
                    Şifremi unuttum
                  </a>
                  <p className="md:flex text-sm text-hzc_beige_700 font-normal">
                    Üye değil misin?
                    <span
                      onClick={() => onSignup?.()}
                      className="text-hzc_purple_500 font-medium text-sm hover:cursor-pointer ms-3 hover:scale-105 transition-all duration-300"
                    >
                      Üye ol
                    </span>
                  </p>
                </div>
              )}
            </>
          )}
        </form>
      </Form>
      <ForgotPasswordDialog
        open={forgotPasswordDialogOpen}
        onOpenChange={setForgotPasswordDialogOpen}
      />
    </div>
  );
}

export function ForgotPasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const FormSchema = z.object({
    email: z.string().regex(emailRegex, 'Geçerli bir e-posta adresi giriniz'),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: '',
    },
  });

  async function onSubmit(values: z.infer<typeof FormSchema>) {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);

      const { data: result } = await Requester.post<GenericResponse<any>>(
        Endpoints.FORGOT_PASSWORD,
        {
          email: values.email,
        },
      );
      if (result?.data?.status) {
        toast({
          variant: 'default',
          description: 'Mail adresinize gönderilen linke tıklayarak şifrenizi sıfırlayabilirsiniz.',
        });
        onOpenChange(false);
      } else {
        toast({
          variant: 'destructive',
          description: 'Bir hata oluştu',
        });
      }
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[32px] text-hzc_purple_500">Şifremi Unuttum</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="text-sm text-hzc_beige_900">
            Aşağıdaki alana hesabınıza bağlı e-posta adresinizi girerek şifre sıfırlama talebinde
            bulunabilirsiniz. Şifre sıfırlama linki, kısa bir süre içinde e-posta adresinize
            gönderilecektir.
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="E-Posta Adresi"
                        className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                        {...field}
                        type="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        inputMode="email"
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <DialogFooter className="gap-2">
                <div className="flex flex-row gap-4 justify-end">
                  <Button
                    className="text-hzc_beige_900 border-1 border-[#5E6AFF1A] bg-[#FFFFFF] hover:bg-[#FFFFFF] transition-all duration-300 hover:scale-105 rounded-full h-[48px]"
                    onClick={() => onOpenChange(false)}
                  >
                    Kapat
                  </Button>
                  <Button
                    className="text-hzc_purple_500 bg-[#5E6AFF1A] hover:bg-[#5E6AFF1A] transition-all duration-300 hover:scale-105 rounded-full h-[48px] gap-2"
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {isLoading && <Spinner className="size-6 text-white" />}
                    Şifreyi Sıfırla
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
